import { createTheme, Theme } from '@mui/material/styles';

// Shipium Brand Colors
const shipiumColors = {
  primary: '#1EBFAB',      // Primary green
  blue: '#2270E1',         // Blue
  darkGreen: '#026458',    // Dark green
  purple: '#67265D',       // Purple
  pink: '#ED6CBA',         // Pink
  yellow: '#FCC31E',       // Yellow
  orange: '#FF6842',       // Orange
  black: '#001529',        // Basic black
  white: '#FFFFFF',        // Basic white
  darkBlue: '#113245',     // Dark blue
  steel: '#3F5F6F',        // Mid-tone steel
  gray: '#BDCACC',         // Basic gray
  lightGray: '#E8ECEC',    // Light accent
  offWhite: '#F6F6F6',     // Off-white background
  offBlue: '#F6F9FF',      // Off-blue background
  mint: '#D6F4EF',         // Primary container (active nav, selected states)
  mintText: '#86E5D9',     // Primary container text on dark surfaces
};

/** Design-system tokens that MUI's palette has no slot for. */
export interface BrandTokens {
  /** Fill for active/selected items (e.g. current nav item). */
  container: string;
  /** Text and icon color on `container`. */
  onContainer: string;
  /** Online/authenticated indicator dot. */
  statusDot: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    brand: BrandTokens;
  }
  interface PaletteOptions {
    brand?: BrandTokens;
  }
}

// Light Theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: shipiumColors.primary,
      light: '#4DD4C7',
      dark: '#149A8A',
      contrastText: shipiumColors.white,
    },
    secondary: {
      main: shipiumColors.blue,
      light: '#5B8FE8',
      dark: '#1A5BC7',
      contrastText: shipiumColors.white,
    },
    background: {
      default: shipiumColors.offBlue,
      paper: shipiumColors.white,
    },
    brand: {
      container: shipiumColors.mint,
      onContainer: shipiumColors.darkGreen,
      statusDot: shipiumColors.primary,
    },
    text: {
      primary: shipiumColors.black,
      secondary: shipiumColors.steel,
    },
    divider: shipiumColors.lightGray,
    action: {
      active: shipiumColors.primary,
      hover: shipiumColors.offBlue,
      selected: shipiumColors.offBlue,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      color: shipiumColors.black,
    },
    h2: {
      color: shipiumColors.black,
    },
    h3: {
      color: shipiumColors.black,
    },
    h4: {
      color: shipiumColors.black,
    },
    h5: {
      color: shipiumColors.black,
    },
    h6: {
      color: shipiumColors.black,
    },
    body1: {
      color: shipiumColors.black,
    },
    body2: {
      color: shipiumColors.steel,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: shipiumColors.white,
          color: shipiumColors.black,
          boxShadow: '0 1px 3px rgba(0, 21, 41, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: shipiumColors.white,
          color: shipiumColors.black,
          borderRight: `1px solid ${shipiumColors.lightGray}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: shipiumColors.white,
          boxShadow: '0 2px 8px rgba(0, 21, 41, 0.1)',
          border: `1px solid ${shipiumColors.lightGray}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
        },
        contained: {
          backgroundColor: shipiumColors.primary,
          '&:hover': {
            backgroundColor: shipiumColors.darkGreen,
          },
        },
        outlined: {
          borderColor: shipiumColors.primary,
          color: shipiumColors.primary,
          '&:hover': {
            backgroundColor: shipiumColors.offBlue,
            borderColor: shipiumColors.darkGreen,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: shipiumColors.lightGray,
            },
            '&:hover fieldset': {
              borderColor: shipiumColors.gray,
            },
            '&.Mui-focused fieldset': {
              borderColor: shipiumColors.primary,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${shipiumColors.lightGray}`,
        },
        head: {
          backgroundColor: shipiumColors.offBlue,
          fontWeight: 600,
        },
      },
    },
  },
});

// Dark Theme
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: shipiumColors.primary,
      light: '#4DD4C7',
      dark: '#149A8A',
      contrastText: shipiumColors.black,
    },
    secondary: {
      main: shipiumColors.blue,
      light: '#5B8FE8',
      dark: '#1A5BC7',
      contrastText: shipiumColors.white,
    },
    background: {
      default: shipiumColors.black,
      paper: shipiumColors.darkBlue,
    },
    brand: {
      container: 'rgba(30, 191, 171, 0.18)',
      onContainer: shipiumColors.mintText,
      statusDot: shipiumColors.primary,
    },
    text: {
      primary: shipiumColors.white,
      secondary: shipiumColors.gray,
    },
    divider: shipiumColors.steel,
    action: {
      active: shipiumColors.primary,
      hover: shipiumColors.steel,
      selected: shipiumColors.steel,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      color: shipiumColors.white,
    },
    h2: {
      color: shipiumColors.white,
    },
    h3: {
      color: shipiumColors.white,
    },
    h4: {
      color: shipiumColors.white,
    },
    h5: {
      color: shipiumColors.white,
    },
    h6: {
      color: shipiumColors.white,
    },
    body1: {
      color: shipiumColors.white,
    },
    body2: {
      color: shipiumColors.gray,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: shipiumColors.darkBlue,
          color: shipiumColors.white,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: shipiumColors.darkBlue,
          color: shipiumColors.white,
          borderRight: `1px solid ${shipiumColors.steel}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: shipiumColors.darkBlue,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          border: `1px solid ${shipiumColors.steel}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
        },
        contained: {
          backgroundColor: shipiumColors.primary,
          '&:hover': {
            backgroundColor: shipiumColors.darkGreen,
          },
        },
        outlined: {
          borderColor: shipiumColors.primary,
          color: shipiumColors.primary,
          '&:hover': {
            backgroundColor: shipiumColors.steel,
            borderColor: shipiumColors.darkGreen,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: shipiumColors.steel,
            },
            '&:hover fieldset': {
              borderColor: shipiumColors.gray,
            },
            '&.Mui-focused fieldset': {
              borderColor: shipiumColors.primary,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${shipiumColors.steel}`,
        },
        head: {
          backgroundColor: shipiumColors.steel,
          fontWeight: 600,
        },
      },
    },
  },
});

export type ThemeMode = 'light' | 'dark';

export const getTheme = (mode: ThemeMode): Theme => {
  return mode === 'light' ? lightTheme : darkTheme;
};

